/**
 * #1034 — per-order cancel for Polymarket US rows in the side rail.
 *
 * Sends through the same per-order US path as the Tracker's Cancel all
 * (`runUsBulkCancel` with one target, #1006): a fresh US client, the account's
 * own-view selection, the real cancel POST, and the same outcome rules —
 * "failed" only when nothing provably went out, everything else uncertain is
 * "unconfirmed", and nothing is ever called canceled. Nothing retries on its
 * own. A cancel that provably never went out plays the order-rejected descent
 * (the trader's alert settings decide whether, and how loud); a requested or
 * unconfirmed one is silent, as a Kalshi cancel from the rail is.
 *
 * Where the rail cannot cancel, the control's place says so ("Can't cancel")
 * with the plain reason in its hover, rather than leaving an empty space.
 *
 * What a row shows comes first from the server's saved cancel attempt (every
 * read carries it); until a read has one, from what this rail just did, so a
 * row never looks untouched between a click and the next read.
 */
import { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { getChannelVolume, isChannelSoundEnabled } from '@/lib/alerts/alertPreferences';
import { playOrderRejectedTone } from '@/lib/audio/tradeAudio';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import { US_CANCEL_UNCONFIRMED_TEXT } from '@/lib/polymarketUs/cancelOutcome';
import { usCancelView } from '@/lib/polymarketUs/portfolioModel';
import type { UsOrderRow, UsReadiness } from '@/lib/polymarketUs/types';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { usReason } from '../PolymarketUs/usVenueLabel';
import { runUsBulkCancel } from '../PositionTracker/usBulkCancelModel';
import type { UsRailAccount } from './usRailModel';

/** What this rail did to one order, until a read carries the server's attempt. */
export type UsRailLocal = { phase: 'sending' } | { phase: 'requested' } | { phase: 'unconfirmed'; code: string | null };

export type UsRailCancelControl =
  | { kind: 'cancel' | 'retry'; label: string | null; title: string }
  /** `blocked`: the rail cannot cancel this order (the title says why). */
  | { kind: 'status'; label: string; title: string; pending: boolean; unconfirmed: boolean; blocked?: true }
  | { kind: 'none' };

const STATUS = (label: string, title: string, pending = false, unconfirmed = false): UsRailCancelControl =>
  ({ kind: 'status', label, title, pending, unconfirmed });
const CANT_CANCEL = (title: string): UsRailCancelControl =>
  ({ kind: 'status', label: 'Can’t cancel', title, pending: false, unconfirmed: false, blocked: true });
const CHECK_IN_POSITIONS = 'Check it under Positions → Polymarket US.';
const unconfirmedTitle = (code: string | null = null) =>
  `${US_CANCEL_UNCONFIRMED_TEXT}${code ? ` (${code})` : ''}. Check status is in Positions → Polymarket US.`;
const REQUESTED_TITLE = 'Cancel requested — awaiting confirmation. Not canceled until the order says so.';

/** Why the rail cannot cancel `row`, in plain words (the code kept in brackets). */
function cantCancelTitle(row: UsOrderRow, readiness: UsReadiness, blocked: string): string {
  if (!row.orderId) {
    return row.receipt.status === 'ready'
      ? `Not at the venue yet; nothing to cancel. ${CHECK_IN_POSITIONS}`
      : `No venue order id yet (the order’s result is unconfirmed), so it can’t be canceled from here. ${CHECK_IN_POSITIONS}`;
  }
  if (readiness.state === 'unavailable') {
    const plain = usReason(readiness.reason);
    return `This account can’t cancel right now: ${plain}${plain.includes(readiness.reason) ? '' : ` (${readiness.reason})`}.`;
  }
  return blocked;
}

/** The one answer for a US row's cancel control. */
export function usRailCancelControl(
  row: UsOrderRow,
  readiness: UsReadiness | null,
  local: UsRailLocal | undefined,
): UsRailCancelControl {
  if (local?.phase === 'sending') return STATUS('Canceling…', 'Cancel request in flight for this Polymarket US order.', true);
  switch (row.cancel?.state) {
    case 'dispatching':
      return STATUS('Canceling…', 'Cancel pending for this Polymarket US order.', true);
    case 'acknowledged':
      return STATUS('Requested', REQUESTED_TITLE);
    case 'unknown':
      return STATUS('Unconfirmed', unconfirmedTitle(), false, true);
    case 'not_sent':
    case undefined:
      break;
  }
  // A read with no attempt, or only a proven-unsent one, can predate what this
  // rail just sent (a poll in flight during the cancel): its local result wins
  // there, so "Retry cancel" never returns for a cancel that went out.
  const stale = !row.cancel || row.cancel.state === 'not_sent';
  if (stale && local?.phase === 'requested') {
    return STATUS('Requested', REQUESTED_TITLE);
  }
  if (stale && local?.phase === 'unconfirmed') {
    return STATUS('Unconfirmed', unconfirmedTitle(local.code), false, true);
  }
  if (!readiness) return CANT_CANCEL('Account readiness unknown');
  // Only a first cancel (or one after a proven-unsent attempt) is offered
  // here, which does not depend on the clock; the timed Repeat cancel stays in
  // the Tracker's US section.
  const view = usCancelView(row, readiness, 0);
  if (!view.canCancel) return view.blockedReason ? CANT_CANCEL(cantCancelTitle(row, readiness, view.blockedReason)) : { kind: 'none' };
  return row.cancel?.state === 'not_sent'
    ? { kind: 'retry', label: 'Retry cancel', title: 'The last cancel didn’t go out — nothing was sent. Cancel again.' }
    : { kind: 'cancel', label: null, title: 'Cancel Polymarket US order' };
}

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const identityNow = () => {
  const { user } = useAuthStore.getState();
  return `${user?.id ?? ''}:${user?.orgId ?? ''}:${getAuthSessionEpoch()}`;
};
const key = (accountId: string, intentId: string) => `${accountId}:${intentId}`;

export function useUsRailCancel(onSettled: () => void) {
  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);
  const who = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const identity = `${who}:${epoch}`;
  const [state, setState] = useState<{ identity: string; local: Record<string, UsRailLocal>; error: string | null }>(
    () => ({ identity, local: {}, error: null }));
  const sending = useRef(new Set<string>());
  const current = state.identity === identity ? state : { identity, local: {}, error: null };

  const update = useCallback((forIdentity: string, fn: (s: typeof state) => typeof state) => {
    setState((s) => {
      // A result from a previous login never lands on this one.
      if (forIdentity !== identityNow()) return s;
      return fn(s.identity === forIdentity ? s : { identity: forIdentity, local: {}, error: null });
    });
  }, []);

  /** `openedUnder`: the login a confirmation was opened under; a change since
   * (login, org, epoch) sends nothing. */
  const cancel = useCallback(async (account: UsRailAccount, row: UsOrderRow, openedUnder?: string) => {
    const k = key(account.accountId, row.intentId);
    if (!account.scope || !row.orderId || sending.current.has(k)) return;
    if (openedUnder !== undefined && openedUnder !== identityNow()) return;
    sending.current.add(k);
    const mine = identityNow();
    update(mine, (s) => ({ ...s, error: null, local: { ...s.local, [k]: { phase: 'sending' } } }));
    try {
      const [result] = await runUsBulkCancel({ portfolio: createUsBrowserPortfolioClient() }, [{
        key: k, accountId: account.accountId, scope: account.scope, intentId: row.intentId, orderId: row.orderId, label: row.target.label,
      }], () => identityNow() === mine);
      // Not sent, provably: refused out loud, as the venue's rejections are.
      if (result.outcome === 'failed' && identityNow() === mine && isChannelSoundEnabled('orderRejected')) {
        playOrderRejectedTone(getChannelVolume('orderRejected'));
      }
      update(mine, (s) => {
        const local = { ...s.local };
        delete local[k];
        if (result.outcome === 'requested') local[k] = { phase: 'requested' };
        if (result.outcome === 'unconfirmed') local[k] = { phase: 'unconfirmed', code: result.code };
        return {
          ...s,
          local,
          error: result.outcome === 'failed'
            ? `Polymarket US cancel didn’t go out — nothing was sent (${
              result.code && result.code !== 'not_sent' ? result.code : 'US_CANCEL_NOT_SENT'}): ${row.target.label}`
            : s.error,
        };
      });
    } finally {
      sending.current.delete(k);
      onSettled();
    }
  }, [update, onSettled]);

  return {
    /** The login this rail's US rows belong to now. */
    identity,
    local: (accountId: string, intentId: string) => current.local[key(accountId, intentId)],
    error: current.error,
    dismissError: () => update(identity, (s) => ({ ...s, error: null })),
    cancel,
  };
}
